import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, css } from "aphrodite";
import PropTypes from 'prop-types'; // Use PropTypes from the standard library
import { DatePicker, Popover, Typography, Calendar, Select, Button, Radio } from "antd";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

// Import dayjs plugins if not already globally available via other means
// If these are required multiple times across files, consider a central setup file
const isSameOrBefore = require("dayjs/plugin/isSameOrBefore");
dayjs.extend(isSameOrBefore);
const isSameOrAfter = require("dayjs/plugin/isSameOrAfter");
dayjs.extend(isSameOrAfter);

export const customDatePickerDateFormat = "MMM DD, YYYY";
export const customDatePickerApiDateFormat = "YYYY-MM-DD";

const customDatePickerStyleSheet = StyleSheet.create({
	popoverContentWrapper: {
		display: "flex",
		flexDirection: "column",
		maxWidth: "777px",
		position: "relative",
		margin: "-12px",
	},
	popoverCalendarsWrapper: {
		display: "flex",
		flexDirection: "row",
		gap: "1px",
		backgroundColor: "#F0F0F0",
	},
	dropdownWrapper: {
		border: "none",
	},
	predefinedDatesWrapper: {
		display: "flex",
		flexDirection: "column",
		padding: "16px",
		gap: "8px",
		backgroundColor: "white",
		width: "fit-content",
		whiteSpace: "nowrap",
	},
	radioButton: {
		marginInlineEnd: "unset",
	},
	popoverFooterWrapper: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-end",
		padding: "9px 12px",
		borderTop: "1px solid #F0F0F0",
	},
	calendarDatePickerWrapper: {
		display: "flex",
		flexDirection: "column",
		width: "350px",
		padding: "16px 20px",
		backgroundColor: "white",
		position: "relative",
	},
	errorText: {
		position: "absolute",
		top: "45px",
		left: "20px",
	},
	calendarHeaderWrapper: {
		display: "flex",
		flexDirection: "row",
		width: "100%",
		padding: "0 8px",
		justifyContent: "space-between",
		alignItems: "center",
	},
	calendarHeaderDropdownsWrapper: {
		display: "flex",
		flexDirection: "row",
		width: "fit-content",
		padding: "10px 0",
	},
	arrowButton: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "0",
		width: "fit-content",
		background: "transparent",
		border: "none",
		boxShadow: "none",
	},
});

// Original functional component structure with props
const CustomDatePicker = ({
	id, // Passed down from Dash wrapper
	startDate, // Expect YYYY-MM-DD string from Dash (via value[0])
	endDate, // Expect YYYY-MM-DD string from Dash (via value[1])
	minDate = '2000-01-01', // Default if not provided
	maxDate = null, // New prop, default null
	onChange, // This is the handleDashChange function from the wrapper
	disabled,
}) => {
	const popoverContentWrapperRef = useRef(null);
	const applyChangesButtonRef = useRef(null);
	const [popoverIsOpen, setPopoverIsOpen] = useState(false);

	const radioButtonGroupRef = useRef(null);
	const [checkedRadioButtonValue, setCheckedRadioButtonValue] = useState(null);
	const [
		currentSelectedCheckedRadioButtonValue,
		setCurrentSelectedCheckedRadioButtonValue,
	] = useState(checkedRadioButtonValue);
	const onRadioButtonChange = e => {
		console.log("Radio button changed:", e.target.value);
		setCurrentSelectedCheckedRadioButtonValue(e.target.value);
	};

	const startDatePickerRef = useRef(null);
	const [startErrorMessage, setStartErrorMessage] = useState(null);
	// Parse incoming dates (expecting YYYY-MM-DD)
	const [currentStartDate, setCurrentStartDate] = useState(startDate ? dayjs(startDate, customDatePickerApiDateFormat) : null);
	const [currentSelectedStartDate, setCurrentSelectedStartDate] = useState(currentStartDate);

	const endDatePickerRef = useRef(null);
	const [endErrorMessage, setEndErrorMessage] = useState(null);
	// Parse incoming dates (expecting YYYY-MM-DD)
	const [currentEndDate, setCurrentEndDate] = useState(endDate ? dayjs(endDate, customDatePickerApiDateFormat) : null);
	const [currentSelectedEndDate, setCurrentSelectedEndDate] = useState(currentEndDate);

	const months = dayjs.months();
	// Parse minDate and maxDate correctly (assuming YYYY-MM-DD)
	const minDateDayjs = dayjs(minDate, customDatePickerApiDateFormat);
	const maxDateDayjs = maxDate ? dayjs(maxDate, customDatePickerApiDateFormat) : null;
	const currentYear = dayjs().year();
	const oldestYear = minDateDayjs.isValid() ? minDateDayjs.year() : 2000; // Fallback year
	const years = Array.from(
		{ length: currentYear + 10 - oldestYear + 1 },
		(_, index) => oldestYear + index,
	);

	const handleKeyDown = event => {
		// Ensure refs are current before accessing properties
		if (!radioButtonGroupRef.current || !startDatePickerRef.current || !endDatePickerRef.current) {
			return;
		}
		const radioButtons = radioButtonGroupRef.current.querySelectorAll('input[type="radio"]');
		let forwardFocusIndex = -1;
		let backWardFocusIndex = radioButtons.length - 1;

		radioButtons.forEach((button, index) => {
			if (button === document.activeElement) {
				forwardFocusIndex = index;
				backWardFocusIndex = index;
			}
		});

		// Simplified focus logic, ensure elements exist before focusing
		const startDateInput = startDatePickerRef.current?.input?.input;
		const endDateInput = endDatePickerRef.current?.input?.input;
		const applyButton = applyChangesButtonRef.current;

		if (event.shiftKey && event.key === "Tab") {
			event.preventDefault();
			if (document.activeElement.type === "radio") {
				if (backWardFocusIndex > 0) {
					radioButtons[backWardFocusIndex - 1]?.focus();
				} else {
					applyButton?.focus();
				}
			}
			if (document.activeElement === startDateInput) {
				backWardFocusIndex = radioButtons.length - 1;
				radioButtons[backWardFocusIndex]?.focus();
			}
			if (document.activeElement === endDateInput) {
				startDateInput?.focus();
			}
			if (document.activeElement === applyButton) {
				endDateInput?.focus();
			}
		}

		if (!event.shiftKey && event.key === "Tab") {
			event.preventDefault();
			if (document.activeElement.type === "radio") {
				if (forwardFocusIndex < radioButtons.length - 1) {
					radioButtons[forwardFocusIndex + 1]?.focus();
				} else {
					startDateInput?.focus();
				}
			}
			if (document.activeElement === startDateInput) {
				endDateInput?.focus();
			}
			if (document.activeElement === endDateInput) {
				applyButton?.focus();
			}
			if (document.activeElement === applyButton) {
				radioButtons[0]?.focus();
			}
		}

		if (document.activeElement.type === "radio") {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				if (forwardFocusIndex < radioButtons.length - 1) {
					radioButtons[forwardFocusIndex + 1]?.focus();
				} else {
					radioButtons[0]?.focus();
				}
			}

			if (event.key === "ArrowUp") {
				event.preventDefault();
				if (forwardFocusIndex > 0) {
					radioButtons[forwardFocusIndex - 1]?.focus();
				} else {
					radioButtons[radioButtons.length - 1]?.focus();
				}
			}
		}

		if (event.key === "Enter" || event.key === " ") {
			if (document.activeElement.type === "radio") {
				event.preventDefault();
				document.activeElement.click();
				startDateInput?.focus();
			}
		}
	};

	useEffect(() => {
		console.log("Effect (radio button value): Running for value:", currentSelectedCheckedRadioButtonValue);
		let calendarStartDate = null;
		let calendarEndDate = dayjs();

		switch (currentSelectedCheckedRadioButtonValue) {
			case "Last 7 Days":
				calendarStartDate = calendarEndDate.subtract(7, "day");
				break;
			case "Last 14 Days":
				calendarStartDate = calendarEndDate.subtract(14, "day");
				break;
			case "Last 30 Days":
				calendarStartDate = calendarEndDate.subtract(30, "day");
				break;
			case "Last 90 Days":
				calendarStartDate = calendarEndDate.subtract(90, "day");
				break;
			case "Last 365 Days":
				calendarStartDate = calendarEndDate.subtract(365, "day");
				break;
			case "Year to Date":
				calendarStartDate = dayjs().startOf("year");
				break;
			default:
				calendarStartDate = null;
		}

		if (currentSelectedCheckedRadioButtonValue !== null && calendarStartDate !== null) {
			if (minDateDayjs.isValid() && calendarStartDate.isBefore(minDateDayjs)) {
				calendarStartDate = minDateDayjs;
			}

			// Also ensure endDate doesn't exceed maxDate if set
			if (maxDateDayjs?.isValid() && calendarEndDate.isAfter(maxDateDayjs)) {
				calendarEndDate = maxDateDayjs;
			}

			// *** Check if dates actually need updating ***
			const startDateChanged = !currentSelectedStartDate || !calendarStartDate.isSame(currentSelectedStartDate, 'day');
			const endDateChanged = !currentSelectedEndDate || !calendarEndDate.isSame(currentSelectedEndDate, 'day');

			if (startDateChanged || endDateChanged) {
				console.log("Effect (radio button value): Dates differ, updating state."); // DEBUG
				if (startDateChanged) setCurrentSelectedStartDate(calendarStartDate);
				if (endDateChanged) setCurrentSelectedEndDate(calendarEndDate);
				console.log("Effect (radio button value): Set dates:", calendarStartDate?.format(customDatePickerApiDateFormat), calendarEndDate?.format(customDatePickerApiDateFormat)); // DEBUG
			} else {
				console.log("Effect (radio button value): Dates are the same, skipping state update."); // DEBUG
			}
		} else {
			console.log("Effect (radio button value): No dates to set (or invalid preset)."); // DEBUG
		}
	}, [currentSelectedCheckedRadioButtonValue, minDateDayjs, maxDateDayjs]);

	useEffect(() => {
		const popoverContent = popoverContentWrapperRef.current;
		if (popoverContent && popoverIsOpen) {
			popoverContent.addEventListener("keydown", handleKeyDown);
		}
		return () => {
			if (popoverContent) {
				popoverContent.removeEventListener("keydown", handleKeyDown);
			}
		};
	}, [popoverIsOpen, handleKeyDown]);

	useEffect(() => {
		console.log(
			"Effect (selected dates): Running. Start:", currentSelectedStartDate?.format(customDatePickerApiDateFormat),
			"End:", currentSelectedEndDate?.format(customDatePickerApiDateFormat)
		);
		// Use parsed minimum date for comparisons
		const minDateString = minDateDayjs.isValid() ? minDateDayjs.format(customDatePickerDateFormat) : minDate;
		const maxDateString = maxDateDayjs?.isValid() ? maxDateDayjs.format(customDatePickerDateFormat) : 'infinity';

		let newStartError = null;
		let newEndError = null;

		if (currentSelectedStartDate && !currentSelectedStartDate.isValid()){
			newStartError = "Invalid start date";
		} else if (minDateDayjs.isValid() && currentSelectedStartDate?.isBefore(minDateDayjs)) {
			newStartError = `Start date cannot be before ${minDateString}`;
		} else if (
			currentSelectedStartDate?.isValid() && currentSelectedEndDate?.isValid() && currentSelectedStartDate.isAfter(currentSelectedEndDate)
		) {
			newStartError = "Start date cannot be after end date";
		} else if (maxDateDayjs?.isValid() && currentSelectedStartDate?.isAfter(maxDateDayjs)) {
			newStartError = `Start date cannot be after ${maxDateString}`;
		}

		if (currentSelectedEndDate && !currentSelectedEndDate.isValid()) {
			newEndError = "Invalid end date";
		} else if (minDateDayjs.isValid() && currentSelectedEndDate?.isBefore(minDateDayjs)) {
			newEndError = `End date cannot be before ${minDateString}`;
		} else if (
			currentSelectedStartDate?.isValid() && currentSelectedEndDate?.isValid() && currentSelectedEndDate.isBefore(currentSelectedStartDate)
		) {
			newEndError = "End date cannot be before start date";
		} else if (maxDateDayjs?.isValid() && currentSelectedEndDate?.isAfter(maxDateDayjs)) {
			newEndError = `End date cannot be after ${maxDateString}`;
		}

		setStartErrorMessage(newStartError);
		setEndErrorMessage(newEndError);
		console.log("Effect (selected dates): Set errors:", newStartError, newEndError);

	}, [currentSelectedStartDate, currentSelectedEndDate, minDateDayjs, maxDateDayjs, minDate]);

	useEffect(() => {
		const newStartDate = startDate ? dayjs(startDate, customDatePickerApiDateFormat) : null;
		const newEndDate = endDate ? dayjs(endDate, customDatePickerApiDateFormat) : null;
		setCurrentStartDate(newStartDate);
		setCurrentEndDate(newEndDate);
		setCurrentSelectedStartDate(newStartDate);
		setCurrentSelectedEndDate(newEndDate);
		setCheckedRadioButtonValue(null);
		setCurrentSelectedCheckedRadioButtonValue(null);
	}, [startDate, endDate]);

	useEffect(() => {
		if (popoverIsOpen) {
			setTimeout(() => {
				if (startDatePickerRef.current) {
					const inputElement = startDatePickerRef.current.input?.input;
					if (inputElement) {
						inputElement.focus();
					}
				}
			}, 100);
		}
	}, [popoverIsOpen]);

	const handleApplyChanges = () => {
		if (startErrorMessage || endErrorMessage) return;

		setPopoverIsOpen(false);
		const newStartDate = currentSelectedStartDate;
		const newEndDate = currentSelectedEndDate;

		setCurrentStartDate(newStartDate);
		setCurrentEndDate(newEndDate);
		setCheckedRadioButtonValue(currentSelectedCheckedRadioButtonValue);

		if (onChange) {
			onChange(
				[
					newStartDate
						? newStartDate.format(customDatePickerApiDateFormat)
						: null,
					newEndDate
						? newEndDate.format(customDatePickerApiDateFormat)
						: null,
				],
				[newStartDate, newEndDate],
			);
		}
	};

	const handleRangePickerChange = (dates) => {
		if (dates === null || (Array.isArray(dates) && dates.length === 0) || (Array.isArray(dates) && dates.every(d => d === null))) {
			setCurrentStartDate(null);
			setCurrentEndDate(null);
			setCurrentSelectedStartDate(null);
			setCurrentSelectedEndDate(null);
			setCheckedRadioButtonValue(null);
			setCurrentSelectedCheckedRadioButtonValue(null);
			if (onChange) {
				onChange([null, null], [null, null]);
			}
		}
	};

	const handlePopoverOpenChange = (newOpen) => {
		if (!newOpen) {
			setCurrentSelectedStartDate(currentStartDate);
			setCurrentSelectedEndDate(currentEndDate);
			setCurrentSelectedCheckedRadioButtonValue(checkedRadioButtonValue);
			setStartErrorMessage(null);
			setEndErrorMessage(null);
		} else {
			setCurrentSelectedStartDate(currentStartDate);
			setCurrentSelectedEndDate(currentEndDate);
			setCurrentSelectedCheckedRadioButtonValue(checkedRadioButtonValue);
		}
		setPopoverIsOpen(newOpen);
	};

	// ---- Helper for DatePicker Blur/KeyDown Validation & State Update ----
	const processDateInput = (inputValue, dateType) => {
		console.log(`processDateInput (${dateType}): Input=`, inputValue); // DEBUG
		const currentSelectedDate = dateType === 'start' ? currentSelectedStartDate : currentSelectedEndDate;
		const setDate = dateType === 'start' ? setCurrentSelectedStartDate : setCurrentSelectedEndDate;
		const setError = dateType === 'start' ? setStartErrorMessage : setEndErrorMessage;
		const otherDate = dateType === 'start' ? currentSelectedEndDate : currentSelectedStartDate;

		if (!inputValue) {
			// If field is empty, clear selection
			console.log(`processDateInput (${dateType}): Input empty, clearing state.`); // DEBUG
			setDate(null);
			setError(null);
			setCurrentSelectedCheckedRadioButtonValue(null);
			return null; // Indicate clear
		} else {
			// Use dayjs's default lenient parsing for typed input
			const parsedDate = dayjs(inputValue);
			console.log(`processDateInput (${dateType}): Parsed=`, parsedDate, "Valid=", parsedDate.isValid()); // DEBUG

			if (parsedDate.isValid()) {
				let dateChanged = !currentSelectedDate || !parsedDate.isSame(currentSelectedDate, 'day');
				console.log(`processDateInput (${dateType}): Parsed date is valid. Changed=`, dateChanged); // DEBUG

				// Validate against min/max/other date *before* setting state
				let newError = null;
				if (minDateDayjs.isValid() && parsedDate.isBefore(minDateDayjs)) {
					newError = `Date cannot be before ${minDateDayjs.format(customDatePickerDateFormat)}`;
				} else if (maxDateDayjs?.isValid() && parsedDate.isAfter(maxDateDayjs)) {
					 newError = `Date cannot be after ${maxDateDayjs.format(customDatePickerDateFormat)}`;
				} else if (otherDate?.isValid()) {
					 if (dateType === 'start' && parsedDate.isAfter(otherDate)) {
						 newError = "Start date cannot be after end date";
					 } else if (dateType === 'end' && parsedDate.isBefore(otherDate)) {
						 newError = "End date cannot be before start date";
					 }
				}
				setError(newError); // Set potential error
				console.log(`processDateInput (${dateType}): Validation error=`, newError); // DEBUG

				// Only update state if valid AND different
				if (newError === null && dateChanged) {
					console.log(`processDateInput (${dateType}): Setting date state.`); // DEBUG
					setDate(parsedDate);
					setCurrentSelectedCheckedRadioButtonValue(null);
					return parsedDate; // Indicate success
				} else if (newError === null && !dateChanged) {
					console.log(`processDateInput (${dateType}): Date valid but unchanged, skipping state update.`); //DEBUG
					return parsedDate; // Indicate success (no change needed)
				} else {
					// Is valid date, but violates range constraints
					 console.log(`processDateInput (${dateType}): Date valid but failed validation, not setting state.`); // DEBUG
					return null; // Indicate validation failure
				}
			} else {
				// Invalid date format entered
				console.log(`processDateInput (${dateType}): Parsed date is invalid.`); // DEBUG
				setError("Invalid Date format");
				return null; // Indicate parse failure
			}
		}
	};

	// ---- Helper for DatePicker Blur ----
	const handleDatePickerBlur = (e, dateType) => {
		processDateInput(e.target.value, dateType);
	};

	// ---- Helper for DatePicker KeyDown ----
	const handleDatePickerKeyDown = (e, dateType) => {
		if (e.key === 'Enter') {
			e.preventDefault(); // Prevent potential form submission
			const processedDate = processDateInput(e.target.value, dateType);
			// Optional: Move focus on successful Enter validation
			if (processedDate) {
				if (dateType === 'start' && endDatePickerRef.current) {
					endDatePickerRef.current.input?.input?.focus();
				} else if (dateType === 'end' && applyChangesButtonRef.current) {
					applyChangesButtonRef.current.focus();
				}
			}
		}
	};

	console.log("Rendering CustomDatePicker. State:", {
		popoverIsOpen,
		checkedRadioButtonValue,
		currentSelectedCheckedRadioButtonValue,
		startErrorMessage,
		endErrorMessage,
		id,
		startDate: startDate,
		endDate: endDate,
		minDate: minDate,
		maxDate: maxDate,
		disabled,
		currentStartDate: currentStartDate?.format(customDatePickerApiDateFormat),
		currentSelectedStartDate: currentSelectedStartDate?.format(customDatePickerApiDateFormat),
		currentEndDate: currentEndDate?.format(customDatePickerApiDateFormat),
		currentSelectedEndDate: currentSelectedEndDate?.format(customDatePickerApiDateFormat),
	});

	return (
		<div id={`${id}-wrapper`}>
			<Popover
				open={popoverIsOpen && !disabled}
				content={
					<div
						ref={popoverContentWrapperRef}
						className={css(customDatePickerStyleSheet.popoverContentWrapper)}
					>
						<div className={css(customDatePickerStyleSheet.popoverCalendarsWrapper)}>
							<Radio.Group
								ref={radioButtonGroupRef}
								onChange={onRadioButtonChange}
								value={currentSelectedCheckedRadioButtonValue}
								className={css(customDatePickerStyleSheet.predefinedDatesWrapper)}
							>
								<Radio className={css(customDatePickerStyleSheet.radioButton)} value={"Last 7 Days"}>Last 7 Days</Radio>
								<Radio className={css(customDatePickerStyleSheet.radioButton)} value={"Last 14 Days"}>Last 14 Days</Radio>
								<Radio className={css(customDatePickerStyleSheet.radioButton)} value={"Last 30 Days"}>Last 30 Days</Radio>
								<Radio className={css(customDatePickerStyleSheet.radioButton)} value={"Last 90 Days"}>Last 90 Days</Radio>
								<Radio className={css(customDatePickerStyleSheet.radioButton)} value={"Last 365 Days"}>Last 365 Days</Radio>
								<Radio className={css(customDatePickerStyleSheet.radioButton)} value={"Year to Date"}>Year to Date</Radio>
							</Radio.Group>
							<div className={css(customDatePickerStyleSheet.calendarDatePickerWrapper)}>
								{!!startErrorMessage && (
									<Text className={css(customDatePickerStyleSheet.errorText)} type="danger">
										{startErrorMessage}
									</Text>
								)}
								<DatePicker
									status={!!startErrorMessage ? "error" : null}
									format={customDatePickerDateFormat}
									allowEmpty={true}
									open={false}
									placeholder={"Start date"}
									value={currentSelectedStartDate}
									ref={startDatePickerRef}
									id={`${id}-startDatePickerInput`}
									onBlur={e => handleDatePickerBlur(e, 'start')}
									onKeyDown={e => handleDatePickerKeyDown(e, 'start')}
									onChange={value => {
										if (value !== null && value.isValid()) {
											if (!currentSelectedStartDate || !value.isSame(currentSelectedStartDate, 'day')) {
												setCurrentSelectedStartDate(value);
												setCurrentSelectedCheckedRadioButtonValue(null);
												setStartErrorMessage(null);
											}
										} else if (value === null) {
											setCurrentSelectedStartDate(null);
											setStartErrorMessage(null);
											setCurrentSelectedCheckedRadioButtonValue(null);
										}
									}}
									allowClear={true}
									disabledDate={(current) => {
										if (!current) return false;
										const beforeMin = minDateDayjs.isValid() && current.isBefore(minDateDayjs, 'day');
										const afterMax = maxDateDayjs?.isValid() && current.isAfter(maxDateDayjs, 'day');
										return beforeMin || afterMax;
									}}
								/>
								<Calendar
									validRange={[
										minDateDayjs.isValid() ? minDateDayjs : null,
										currentSelectedEndDate?.isValid() ? currentSelectedEndDate : (maxDateDayjs?.isValid() ? maxDateDayjs : null)
									]}
									headerRender={({ value, onChange: onHeaderChange }) => (
										<div className={css(customDatePickerStyleSheet.calendarHeaderWrapper)}>
											<Button
												className={css(customDatePickerStyleSheet.arrowButton)}
												icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="40" viewBox="0 0 22 40" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M12.0607 16.0607L11 15L6 20L11 25L12.0607 23.9393L8.12132 20L12.0607 16.0607Z" fill="black" fillOpacity="0.25" /></svg>}
												onClick={() => { onHeaderChange(value.subtract(1, "month")); }}
												disabled={value?.isSameOrAfter(currentSelectedEndDate || maxDateDayjs || dayjs(), "month")}
											/>
											<div className={css(customDatePickerStyleSheet.calendarHeaderDropdownsWrapper)}>
												<Select
													variant="borderless"
													suffixIcon={<svg style={{ marginTop: "auto" }} xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z" fill="black" fillOpacity="0.25" /></svg>}
													value={months[value?.month()]}
													onChange={monthValue => {
														onHeaderChange(value.month(months.indexOf(monthValue)));
														setCurrentSelectedCheckedRadioButtonValue(null);
													}}
													dropdownStyle={{ width: "105px" }}
												>
													{months?.map(month => <Option key={month} value={month}>{month}</Option>)}
												</Select>
												<Select
													key={value?.year()}
													variant="borderless"
													suffixIcon={<svg style={{ marginTop: "auto" }} xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z" fill="black" fillOpacity="0.25" /></svg>}
													value={value?.year()}
													onChange={yearValue => {
														onHeaderChange(value.year(yearValue));
														setCurrentSelectedCheckedRadioButtonValue(null);
													}}
												>
													{years?.map(year => <Option key={year} value={year}>{year}</Option>)}
												</Select>
											</div>
											<Button
												className={css(customDatePickerStyleSheet.arrowButton)}
												icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="40" viewBox="0 0 22 40" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M9.93934 16.0607L11 15L16 20L11 25L9.93934 23.9393L13.8787 20L9.93934 16.0607Z" fill="black" fillOpacity="0.25" /></svg>}
												onClick={() => { onHeaderChange(value.add(1, "month")); }}
												disabled={value?.isSameOrAfter(currentSelectedEndDate || maxDateDayjs || dayjs(), "month")}
											/>
										</div>
									)}
									fullscreen={false}
									onSelect={date => {
										if (!currentSelectedStartDate || !date.isSame(currentSelectedStartDate, 'day')) {
											setCurrentSelectedStartDate(date);
											setCurrentSelectedCheckedRadioButtonValue(null);
											startDatePickerRef.current?.input?.input?.focus();
										}
									}}
									value={currentSelectedStartDate}
								/>
							</div>

							<div className={css(customDatePickerStyleSheet.calendarDatePickerWrapper)}>
								{!!endErrorMessage && (
									<Text className={css(customDatePickerStyleSheet.errorText)} type="danger">
										{endErrorMessage}
									</Text>
								)}
								<DatePicker
									status={!!endErrorMessage ? "error" : null}
									format={customDatePickerDateFormat}
									allowEmpty={true}
									open={false}
									placeholder={"End date"}
									value={currentSelectedEndDate}
									ref={endDatePickerRef}
									id={`${id}-endDatePickerInput`}
									onBlur={e => handleDatePickerBlur(e, 'end')}
									onKeyDown={e => handleDatePickerKeyDown(e, 'end')}
									onChange={value => {
										if (value !== null && value.isValid()) {
											if (!currentSelectedEndDate || !value.isSame(currentSelectedEndDate, 'day')) {
												setCurrentSelectedEndDate(value);
												setCurrentSelectedCheckedRadioButtonValue(null);
												setEndErrorMessage(null);
											}
										} else if (value === null) {
											setCurrentSelectedEndDate(null);
											setEndErrorMessage(null);
											setCurrentSelectedCheckedRadioButtonValue(null);
										}
									}}
									allowClear={true}
									disabledDate={(current) => {
										if (!current) return false;
										const beforeMinOrStart = (currentSelectedStartDate?.isValid() ? current.isBefore(currentSelectedStartDate, 'day') : false) || (minDateDayjs.isValid() && current.isBefore(minDateDayjs, 'day'));
										const afterMax = maxDateDayjs?.isValid() && current.isAfter(maxDateDayjs, 'day');
										return beforeMinOrStart || afterMax;
									}}
								/>
								<Calendar
									validRange={[
										currentSelectedStartDate?.isValid() ? currentSelectedStartDate : (minDateDayjs.isValid() ? minDateDayjs : null),
										maxDateDayjs?.isValid() ? maxDateDayjs : null
									]}
									headerRender={({ value, onChange: onHeaderChange }) => (
										<div className={css(customDatePickerStyleSheet.calendarHeaderWrapper)}>
											<Button
												className={css(customDatePickerStyleSheet.arrowButton)}
												icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="40" viewBox="0 0 22 40" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M12.0607 16.0607L11 15L6 20L11 25L12.0607 23.9393L8.12132 20L12.0607 16.0607Z" fill="black" fillOpacity="0.25" /></svg>}
												onClick={() => { onHeaderChange(value.subtract(1, "month")); }}
												disabled={value?.isSameOrAfter(maxDateDayjs || dayjs(), "month")}
											/>
											<div className={css(customDatePickerStyleSheet.calendarHeaderDropdownsWrapper)}>
												<Select
													variant="borderless"
													suffixIcon={<svg style={{ marginTop: "auto" }} xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z" fill="black" fillOpacity="0.25" /></svg>}
													value={months[value?.month()]}
													onChange={monthValue => {
														onHeaderChange(value.month(months.indexOf(monthValue)));
														setCurrentSelectedCheckedRadioButtonValue(null);
													}}
													dropdownStyle={{ width: "105px" }}
												>
													{months?.map(month => <Option key={month} value={month}>{month}</Option>)}
												</Select>
												<Select
													key={value?.year()}
													variant="borderless"
													suffixIcon={<svg style={{ marginTop: "auto" }} xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M1.06066 0.969613L-2.18557e-07 2.03027L5 7.03027L10 2.03027L8.93934 0.969613L5 4.90895L1.06066 0.969613Z" fill="black" fillOpacity="0.25" /></svg>}
													value={value?.year()}
													onChange={yearValue => {
														onHeaderChange(value.year(yearValue));
														setCurrentSelectedCheckedRadioButtonValue(null);
													}}
												>
													{years?.map(year => <Option key={year} value={year}>{year}</Option>)}
												</Select>
											</div>
											<Button
												className={css(customDatePickerStyleSheet.arrowButton)}
												icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="40" viewBox="0 0 22 40" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M9.93934 16.0607L11 15L16 20L11 25L9.93934 23.9393L13.8787 20L9.93934 16.0607Z" fill="black" fillOpacity="0.25" /></svg>}
												onClick={() => { onHeaderChange(value.add(1, "month")); }}
												disabled={value?.isSameOrAfter(maxDateDayjs || dayjs(), "month")}
											/>
										</div>
									)}
									fullscreen={false}
									onSelect={date => {
										if (!currentSelectedEndDate || !date.isSame(currentSelectedEndDate, 'day')) {
											setCurrentSelectedEndDate(date);
											setCurrentSelectedCheckedRadioButtonValue(null);
											endDatePickerRef.current?.input?.input?.focus();
										}
									}}
									value={currentSelectedEndDate}
								/>
							</div>
						</div>
						<div className={css(customDatePickerStyleSheet.popoverFooterWrapper)}>
							<Button
								disabled={!!startErrorMessage || !!endErrorMessage}
								ref={applyChangesButtonRef}
								id={`${id}-applyChangesButton`}
								onClick={handleApplyChanges}
								type="primary"
								onKeyDown={event => {
									if (document.activeElement === applyChangesButtonRef.current) {
										if (!event.shiftKey && event.key === "Tab") {
											event.preventDefault();
											radioButtonGroupRef.current?.querySelectorAll('input[type="radio"]')[0]?.focus();
										}
										if (event.shiftKey && event.key === "Tab") {
											event.preventDefault();
											endDatePickerRef.current?.input?.input?.focus();
										}
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											if (!applyChangesButtonRef.current?.disabled) {
												handleApplyChanges();
											}
										}
									}
								}}
							>
								{"Apply"}
							</Button>
						</div>
					</div>
				}
				trigger="click"
				placement="bottomLeft"
				onOpenChange={handlePopoverOpenChange}
			>
				<RangePicker
					id={`${id}-rangePickerTrigger`}
					format={customDatePickerDateFormat}
					disabled={disabled}
					open={false}
					allowEmpty={[true, true]}
					value={[currentStartDate, currentEndDate]}
					onChange={handleRangePickerChange}
					allowClear={true}
				/>
			</Popover>
		</div>
	);
};

CustomDatePicker.propTypes = {
	id: PropTypes.string.isRequired,
	startDate: PropTypes.string, // Expect YYYY-MM-DD string from Dash wrapper
	endDate: PropTypes.string, // Expect YYYY-MM-DD string from Dash wrapper
	minDate: PropTypes.string, // Renamed from minimumDate
	maxDate: PropTypes.string, // New prop
	onChange: PropTypes.func.isRequired, // Provided by the wrapper
	disabled: PropTypes.bool,
};

export default CustomDatePicker; 